import type { Log } from '@montugroup/observability';
import { ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { ATTR_HOST_NAME } from '@opentelemetry/semantic-conventions/incubating';
import { DateTime } from 'luxon';
import { desensitizeObjectValues } from './logger-utils';
import type { LogAttributes, Logger } from './logger.interface';
import { LogLevel, LogLevelText } from './logger.interface';

export const createServerLogger = (name: string, customerId?: string, hostname?: string) =>
  new ServerLogger(name, customerId, hostname);

class ServerLogger implements Logger {
  constructor(
    private name: string,
    private customerId?: string,
    private hostname?: string,
  ) {}

  fatal(message: string, attributes?: LogAttributes): void {
    this.log(LogLevel.FATAL, message, attributes);
  }

  critical(message: string, attributes?: LogAttributes): void {
    this.log(LogLevel.CRITICAL, message, attributes);
  }

  debug(message: string, attributes?: LogAttributes): void {
    this.log(LogLevel.DEBUG, message, attributes);
  }

  error(message: string, attributes?: LogAttributes): void {
    this.log(LogLevel.ERROR, message, attributes);
  }

  info(message: string, attributes?: LogAttributes): void {
    this.log(LogLevel.INFO, message, attributes);
  }

  trace(message: string, attributes?: LogAttributes): void {
    this.log(LogLevel.TRACE, message, attributes);
  }

  warn(message: string, attributes?: LogAttributes): void {
    this.log(LogLevel.WARN, message, attributes);
  }

  private log(level: LogLevel, message: string, attributes: LogAttributes = {}) {
    /**
     * Do not log messages below level INFO to DataDog.
     */
    if (level < LogLevel.INFO) {
      return;
    }

    /**
     * Redact sensitive information from attributes. These are then sent as a
     * separate object, which allows them to be queried in DataDog.
     */
    const desensitizedAttributes = attributes ? desensitizeObjectValues(attributes) : undefined;

    const log: Log = {
      Timestamp: DateTime.now().toMillis(),
      Attributes: {
        loggerName: this.name,
        ...desensitizedAttributes,
        ...(this.customerId
          ? {
              usr: {
                id: this.customerId,
              },
            }
          : {}),
      },
      Resource: {
        [ATTR_HOST_NAME]: this.hostname,
        [ATTR_SERVICE_VERSION]: process.env.DD_VERSION || 'unknown',
      },
      Body: message,
      SeverityText: LogLevelText[level],
    };

    try {
      const serializedLog = JSON.stringify(log);
      /**
       * We prefix the log with some text so that Datadog treats it as full text
       * first. It then extracts the JSON object out with all of its attributes
       * giving us full text search and indexed attributes across logs produced
       * to CW from ECS and Lambda. The Lambda pushes logs into DataDog. Once in
       * DataDog, the Pipelines extract the JSON object and its attributes for
       * indexing and searching.
       *
       * Do not make changes to the format of the log, since the DataDog
       * Pipeline relies on this exact format to correctly extract the JSON
       * object and its attributes.
       */
      process.stdout.write(`${log.SeverityText} ${serializedLog} \n`);
    } catch (e) {
      process.stdout.write(
        `${LogLevelText[LogLevel.CRITICAL]} ${JSON.stringify({
          Timestamp: Date.now(),
          SeverityText: LogLevelText[LogLevel.CRITICAL],
          Body: 'Failed to serialize log',
          Resource: {
            [ATTR_HOST_NAME]: this.hostname,
            [ATTR_SERVICE_VERSION]: process.env.DD_VERSION || 'unknown',
          },
          Attributes: {
            error:
              e instanceof Error
                ? {
                    message: e.message,
                    stack: e.stack,
                    type: e.name,
                  }
                : e,
            loggerName: 'ConsoleLogger',
          },
        })} \n`,
      );
    }
  }
}
